import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { Role } from '../../types';
import type { AdminUser } from '../../types/admin';
import { usersApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';

interface UserFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  user: AdminUser | null;
  roles: Role[];
  onClose: () => void;
  onSaved: () => void;
}

/** Hộp thoại tạo mới / chỉnh sửa người dùng + gán vai trò (chip đa lựa chọn). */
export function UserFormDialog({
  open,
  mode,
  user,
  roles,
  onClose,
  onSaved,
}: UserFormDialogProps): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && user) {
      setName(user.name);
      setEmail(user.email);
      setUsername(user.username ?? '');
      setSelectedRoleIds((user.roles ?? []).map((r) => r.id));
    } else {
      setName('');
      setEmail('');
      setUsername('');
      setSelectedRoleIds([]);
    }
    setPassword('');
    setError(null);
  }, [open, mode, user]);

  const handleRoleChange = (e: SelectChangeEvent<string[]>) => {
    const value = e.target.value;
    setSelectedRoleIds(typeof value === 'string' ? value.split(',') : value);
  };

  const roleName = (id: string) => roles.find((r) => r.id === id)?.name ?? id;

  const validate = (): string | null => {
    if (!name.trim()) return 'Vui lòng nhập họ tên';
    if (!email.trim() && !username.trim()) return 'Vui lòng nhập email hoặc tên đăng nhập';
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return 'Email không hợp lệ (ví dụ: user@example.com)';
    }
    if (username.trim() && !/^[a-zA-Z0-9_-]{3,32}$/.test(username.trim())) {
      return 'Tên đăng nhập phải 3-32 ký tự, chỉ gồm chữ/số/gạch dưới/gạch ngang';
    }
    if (mode === 'create' && password.length < 8) {
      return 'Mật khẩu phải có tối thiểu 8 ký tự';
    }
    return null;
  };

  const syncRoles = async (userId: string, previousRoleIds: string[]) => {
    const prevSet = new Set(previousRoleIds);
    const nextSet = new Set(selectedRoleIds);
    const toAdd = selectedRoleIds.filter((id) => !prevSet.has(id));
    const toRemove = previousRoleIds.filter((id) => !nextSet.has(id));
    await Promise.all([
      ...toAdd.map((roleId) => usersApi.assignRole(userId, roleId)),
      ...toRemove.map((roleId) => usersApi.removeRole(userId, roleId)),
    ]);
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (mode === 'create') {
        await usersApi.create({
          email: email.trim() || undefined,
          username: username.trim() || null,
          name: name.trim(),
          password,
          role_ids: selectedRoleIds.length > 0 ? selectedRoleIds : undefined,
        });
        notify('Đã tạo người dùng mới', 'success');
      } else if (user) {
        await usersApi.update(user.id, {
          email: email.trim(),
          username: username.trim() || null,
          name: name.trim(),
        });
        await syncRoles(user.id, (user.roles ?? []).map((r) => r.id));
        notify('Đã cập nhật người dùng', 'success');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Không thể lưu người dùng'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Thêm người dùng' : 'Sửa người dùng'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Họ tên"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            disabled={saving}
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            helperText="Bỏ trống nếu chỉ dùng tên đăng nhập"
            disabled={saving}
          />
          <TextField
            label="Tên đăng nhập (tuỳ chọn)"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            helperText="3-32 ký tự, chỉ chữ/số/gạch dưới/gạch ngang — dùng thay email khi đăng nhập"
            disabled={saving}
          />
          {mode === 'create' && (
            <TextField
              label="Mật khẩu"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              required
              helperText="Tối thiểu 8 ký tự"
              disabled={saving}
            />
          )}
          <FormControl fullWidth disabled={saving}>
            <InputLabel id="user-roles-label">Vai trò</InputLabel>
            <Select
              labelId="user-roles-label"
              label="Vai trò"
              multiple
              value={selectedRoleIds}
              onChange={handleRoleChange}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((id) => (
                    <Chip key={id} label={roleName(id)} size="small" />
                  ))}
                </Box>
              )}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {error && (
            <Box sx={{ color: 'error.main', fontSize: 14 }}>{error}</Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Hủy
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {mode === 'create' ? 'Tạo mới' : 'Lưu thay đổi'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
